import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEye,
  faImage,
  faPalette,
  faImages,
  faRotate,
} from '@fortawesome/free-solid-svg-icons';

import { ROUTES } from '@/constants';
import { Paginator, Spinner } from '@/components/ui';
import { useRendersPage, PRESENTATION_KEYS, CONTENT_LIST_PAGE_SIZE } from '@/api';
import { StatusBadge, fmtDate } from '@/components/ContentList';
import styles from './ItemsList.module.css';

const PAGE_SIZE = CONTENT_LIST_PAGE_SIZE;

export function RendersList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setHeaderTitle, setHeaderSlot } = useOutletContext<{
    setHeaderTitle: (title: string) => void;
    setHeaderSlot: (slot: React.ReactNode) => void;
  }>();
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading, isFetching, isError } = useRendersPage(currentPage);

  useEffect(() => {
    if (isError) {
      toast.error('Error loading renders. Please try again.');
    }
  }, [isError]);

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: PRESENTATION_KEYS.all('render') });
  }, [queryClient]);

  const totalPages = data ? Math.ceil(data.count / PAGE_SIZE) : 1;

  useEffect(() => {
    const countStr = data ? ` (${data.count})` : '';
    setHeaderTitle(`Renders${countStr}`);
    setHeaderSlot(
      <div className={styles.headerSlot}>
        <button className={styles.refreshBtn} onClick={handleRefresh} title="Refresh">
          <FontAwesomeIcon icon={faRotate} spin={isFetching} />
        </button>
        <button
          className={styles.newBtnHeader}
          onClick={() => navigate(ROUTES.dashboardGenerateRenders)}
        >
          <FontAwesomeIcon icon={faPlus} />
          <span className={styles.hideOnMobile}>Generate New</span>
        </button>
      </div>,
    );
    return () => {
      setHeaderTitle('');
      setHeaderSlot(null);
    };
  }, [setHeaderTitle, setHeaderSlot, data, navigate, isFetching, handleRefresh]);

  return (
    <div className={styles.page}>
      {isLoading ? (
        <div className={styles.loadingState}>
          <Spinner />
        </div>
      ) : data?.results.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <FontAwesomeIcon icon={faImages} />
          </div>
          <p className={styles.emptyTitle}>No renders yet</p>
          <p className={styles.emptyText}>
            Generate photorealistic 3D venue renders for your events.
          </p>
          <button
            className={styles.emptyBtn}
            onClick={() => navigate(ROUTES.dashboardGenerateRenders)}
          >
            <FontAwesomeIcon icon={faPlus} /> Generate Renders
          </button>
        </div>
      ) : (
        <div className={styles.cardList}>
          {data?.results.map((item) => {
            const thumbnail = item.generated_images?.[0] ?? null;
            const theme = (item.context_data?.theme as string) ?? '';
            const primary = (item.context_data?.primaryColor as string) || '#0a0a0a';
            const secondary = (item.context_data?.secondaryColor as string) || '#D4AF37';
            const imgCount = item.generated_images?.length ?? 0;
            const statusCls =
              (styles[`status_${item.status}` as keyof typeof styles] as string) ?? '';

            return (
              <div
                key={item.id}
                className={`${styles.card} ${statusCls}`}
                onClick={() =>
                  navigate(ROUTES.dashboardRenderDetail.replace(':id', String(item.id)))
                }
              >
                <div className={styles.presThumb}>
                  {thumbnail ? (
                    <img src={thumbnail} alt={item.name} className={styles.thumbImg} />
                  ) : (
                    <div
                      className={styles.thumbPlaceholder}
                      style={{
                        background: `linear-gradient(160deg, ${primary} 0%, ${secondary} 100%)`,
                      }}
                    >
                      <FontAwesomeIcon icon={faImage} />
                    </div>
                  )}
                </div>

                <div className={styles.cardBody}>
                  <div className={styles.cardTop}>
                    <h3 className={styles.cardName}>{item.name}</h3>
                    <StatusBadge
                      status={item.status}
                      since={item.created}
                      styles={styles as Record<string, string>}
                    />
                  </div>

                  <div className={styles.cardDetails}>
                    {theme && (
                      <span className={styles.detailItem}>
                        <FontAwesomeIcon icon={faPalette} className={styles.detailIcon} />
                        {theme}
                      </span>
                    )}
                    {imgCount > 0 && (
                      <span className={styles.detailItem}>
                        <FontAwesomeIcon icon={faImages} className={styles.detailIcon} />
                        {imgCount} render{imgCount !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>

                  <div className={styles.cardFooter}>
                    Created {fmtDate(item.created)}
                    {item.created_by_email && (
                      <span className={styles.cardCreatedBy}>by {item.created_by_email}</span>
                    )}
                  </div>
                </div>

                <div className={styles.cardActions} onClick={(e) => e.stopPropagation()}>
                  <button
                    className={`${styles.actionBtn} ${styles.actionBtnPrimary}`}
                    onClick={() =>
                      navigate(ROUTES.dashboardRenderDetail.replace(':id', String(item.id)))
                    }
                  >
                    <FontAwesomeIcon icon={faEye} /> View
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className={styles.paginatorWrap}>
          <Paginator page={currentPage} numPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      )}
    </div>
  );
}
